import {
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  SimpleGrid,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { Select } from 'chakra-react-select';
import { zodResolver } from '@hookform/resolvers/zod';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import {
  FormContentContainer,
  FormFooterContainer,
  FormMainContainer,
} from '@duke-hq/ui/components/Modals/FormModalLayouts';
import {
  EmployerContactLogFormSchema,
  NEW_CONTACT_VALUE,
  type EmployerContactLogFormValues,
  type EmployerContactLogWritePayload,
} from '@duke-hq/svc-api/admin/trpc/routers/workPlacements/employers/schemas';
import { testIds } from '../testIds';
import {
  contactLogResultMeta,
  contactLogResultOrder,
  contactLogTypeMeta,
  contactLogTypeOrder,
} from './contactLogTypeMeta';
import { nowForDateTimeLocal } from './dateTimeLocal';

type ContactOption = { id: string; firstName: string; lastName: string };

const emptyDefaults: EmployerContactLogFormValues = {
  type: 'INBOUND_CALL',
  occurredAt: '',
  result: '',
  contactSelection: '',
  newFirstName: '',
  newLastName: '',
  newEmail: '',
  newPhone: '',
  addToContacts: true,
  notes: '',
};

export function LogContactForm({
  contacts,
  removedContact,
  initialValues,
  isEditing = false,
  onSave,
  onCancel,
  isSaving,
}: {
  contacts: ContactOption[];
  removedContact?: { id: string; name: string } | null;
  initialValues?: Partial<EmployerContactLogFormValues>;
  isEditing?: boolean;
  onSave: (payload: EmployerContactLogWritePayload) => void;
  onCancel: () => void;
  isSaving: boolean;
}): React.ReactElement {
  const methods = useForm<EmployerContactLogFormValues>({
    resolver: zodResolver(EmployerContactLogFormSchema),
    reValidateMode: 'onSubmit',
    defaultValues: { ...emptyDefaults, occurredAt: nowForDateTimeLocal(), ...initialValues },
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = methods;

  const contactSelection = useWatch({ control, name: 'contactSelection' });
  const isNewContact = contactSelection === NEW_CONTACT_VALUE;

  const typeOptions = contactLogTypeOrder.map((type) => ({
    value: type,
    label: contactLogTypeMeta[type].label(),
  }));

  const resultOptions = contactLogResultOrder.map((result) => ({
    value: result,
    label: contactLogResultMeta[result].label(),
  }));

  const showRemovedContact =
    removedContact != null && !contacts.some((contact) => contact.id === removedContact.id);

  const contactOptions = [
    ...contacts.map((contact) => ({
      value: contact.id,
      label: `${contact.firstName} ${contact.lastName}`.trim(),
    })),
    ...(showRemovedContact && removedContact != null
      ? [{ value: removedContact.id, label: t`${removedContact.name} (removed from directory)` }]
      : []),
    { value: NEW_CONTACT_VALUE, label: t`New contact...` },
  ];

  const onSubmit = (values: EmployerContactLogFormValues) => {
    const contact =
      values.contactSelection === ''
        ? ({ mode: 'none' } as const)
        : values.contactSelection === NEW_CONTACT_VALUE
          ? ({
              mode: 'new',
              firstName: values.newFirstName.trim(),
              lastName: values.newLastName.trim(),
              email: values.newEmail.trim() === '' ? null : values.newEmail.trim(),
              phone: values.newPhone.trim() === '' ? null : values.newPhone.trim(),
              addToContacts: values.addToContacts,
            } as const)
          : ({ mode: 'existing', contactId: values.contactSelection } as const);

    onSave({
      type: values.type,
      // Send the wall-clock string with the viewer's IANA timezone; the backend
      // resolves it to a UTC instant. A bare Date would be read as UTC and shift.
      occurredAt: values.occurredAt,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      result: values.result === '' ? null : values.result,
      notes: values.notes.trim() === '' ? null : values.notes.trim(),
      contact,
    });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%', height: '100%' }} noValidate>
        <FormMainContainer>
          <FormContentContainer>
            <VStack align="stretch" spacing={4} w="full">
              <Heading fontSize="lg" size="xs" w="full">
                {isEditing ? <Trans>Edit contact</Trans> : <Trans>Log contact</Trans>}
              </Heading>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl isRequired isInvalid={errors.type != null}>
                  <FormLabel>
                    <Trans>Type</Trans>
                  </FormLabel>
                  <Controller
                    control={control}
                    name="type"
                    render={({ field }) => (
                      <Select
                        options={typeOptions}
                        value={typeOptions.find((option) => option.value === field.value)}
                        onChange={(selected) => field.onChange(selected?.value)}
                        onBlur={field.onBlur}
                        inputId={testIds.logContactTypeSelect}
                      />
                    )}
                  />
                </FormControl>

                <FormControl isRequired isInvalid={errors.occurredAt != null}>
                  <FormLabel>
                    <Trans>Date &amp; time</Trans>
                  </FormLabel>
                  <Input
                    type="datetime-local"
                    {...register('occurredAt')}
                    data-testid={testIds.logContactDateTimeInput}
                  />
                  <FormErrorMessage>{errors.occurredAt?.message}</FormErrorMessage>
                </FormControl>
              </SimpleGrid>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl>
                  <FormLabel>
                    <Trans>Result</Trans>
                  </FormLabel>
                  <Controller
                    control={control}
                    name="result"
                    render={({ field }) => (
                      <Select
                        placeholder={t`Select a result`}
                        options={resultOptions}
                        value={resultOptions.find((option) => option.value === field.value) ?? null}
                        onChange={(selected) => field.onChange(selected?.value ?? '')}
                        onBlur={field.onBlur}
                        inputId={testIds.logContactResultInput}
                      />
                    )}
                  />
                </FormControl>
              </SimpleGrid>

              <FormControl>
                <FormLabel>
                  <Trans>Notes</Trans>
                </FormLabel>
                <Textarea
                  {...register('notes')}
                  placeholder={t`Add any notes about the contact`}
                  data-testid={testIds.logContactNotesInput}
                />
              </FormControl>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl>
                  <FormLabel>
                    <Trans>Contact</Trans>
                  </FormLabel>
                  <Controller
                    control={control}
                    name="contactSelection"
                    render={({ field }) => (
                      <Select
                        placeholder={t`Select contact`}
                        options={contactOptions}
                        value={
                          contactOptions.find((option) => option.value === field.value) ?? null
                        }
                        onChange={(selected) => field.onChange(selected?.value ?? '')}
                        onBlur={field.onBlur}
                        inputId={testIds.logContactContactSelect}
                      />
                    )}
                  />
                </FormControl>
              </SimpleGrid>

              {isNewContact ? (
                <>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl isRequired isInvalid={errors.newFirstName != null}>
                      <FormLabel>
                        <Trans>First name</Trans>
                      </FormLabel>
                      <Input
                        {...register('newFirstName')}
                        placeholder={t`First name`}
                        data-testid={testIds.newContactFirstNameInput}
                      />
                      <FormErrorMessage>{errors.newFirstName?.message}</FormErrorMessage>
                    </FormControl>

                    <FormControl isRequired isInvalid={errors.newLastName != null}>
                      <FormLabel>
                        <Trans>Last name</Trans>
                      </FormLabel>
                      <Input
                        {...register('newLastName')}
                        placeholder={t`Last name`}
                        data-testid={testIds.newContactLastNameInput}
                      />
                      <FormErrorMessage>{errors.newLastName?.message}</FormErrorMessage>
                    </FormControl>

                    <FormControl isInvalid={errors.newEmail != null}>
                      <FormLabel>
                        <Trans>Email address</Trans>
                      </FormLabel>
                      <Input
                        type="email"
                        {...register('newEmail')}
                        placeholder={t`Email address`}
                        data-testid={testIds.newContactEmailInput}
                      />
                      <FormErrorMessage>{errors.newEmail?.message}</FormErrorMessage>
                    </FormControl>

                    <FormControl isInvalid={errors.newPhone != null}>
                      <FormLabel>
                        <Trans>Phone number</Trans>
                      </FormLabel>
                      <Input
                        {...register('newPhone')}
                        placeholder={t`Phone number`}
                        data-testid={testIds.newContactPhoneInput}
                      />
                      <FormErrorMessage>{errors.newPhone?.message}</FormErrorMessage>
                    </FormControl>
                  </SimpleGrid>

                  <FormControl>
                    <Checkbox
                      {...register('addToContacts')}
                      colorScheme="brand"
                      data-testid={testIds.addToContactsToggle}
                    >
                      <Trans>Add to employer contacts</Trans>
                    </Checkbox>
                  </FormControl>
                </>
              ) : null}
            </VStack>
          </FormContentContainer>

          <FormFooterContainer>
            <Button
              type="submit"
              variant="primary"
              isLoading={isSaving}
              data-testid={testIds.saveLogContactButton}
            >
              <Trans>Save</Trans>
            </Button>
            <Button variant="secondary" onClick={onCancel} isDisabled={isSaving}>
              <Trans>Cancel</Trans>
            </Button>
          </FormFooterContainer>
        </FormMainContainer>
      </form>
    </FormProvider>
  );
}
